import type { CguAcceptanceRecord, CguStatus } from '@domain/legal/CguAcceptance';
import { latestAcceptanceForUser, toCguStatus } from '@domain/legal/CguAcceptance';
import { CGU_VERSION } from '@domain/legal/CguVersion';
import { uuid } from '@shared/utils/uuid';

const MIRROR_KEY = 'nexxus_cgu_acceptances_v1';

function readMirror(): CguAcceptanceRecord[] {
  try {
    const raw = localStorage.getItem(MIRROR_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CguAcceptanceRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeMirror(rows: CguAcceptanceRecord[]): void {
  try {
    localStorage.setItem(MIRROR_KEY, JSON.stringify(rows));
  } catch {
    /* quota / private */
  }
}

function appendMirror(row: CguAcceptanceRecord): void {
  const rows = readMirror();
  rows.push(row);
  writeMirror(rows);
}

export async function getCguStatus(userId: string): Promise<CguStatus> {
  const latest = await getLatestCguAcceptance(userId);
  return toCguStatus(latest, CGU_VERSION);
}

export async function getLatestCguAcceptance(userId: string): Promise<CguAcceptanceRecord | null> {
  const ipc = window.electron?.cguLatest;
  if (ipc) {
    try {
      const row = await ipc(userId);
      if (row && typeof row.version_cgu === 'string') return row;
    } catch {
      /* repli miroir */
    }
  }
  return latestAcceptanceForUser(readMirror(), userId);
}

export async function acceptCgu(input: {
  user_id: string;
  version_cgu?: string;
  ip_address?: string | null;
  user_agent?: string | null;
  is_trial?: boolean;
}): Promise<{ success: boolean }> {
  const record: CguAcceptanceRecord = {
    id: uuid('cgu'),
    user_id: input.user_id,
    version_cgu: input.version_cgu ?? CGU_VERSION,
    accepted_at: new Date().toISOString(),
    ip_address: input.ip_address ?? 'local',
    user_agent: input.user_agent ?? (typeof navigator === 'undefined' ? null : navigator.userAgent),
    is_trial: input.is_trial === true,
  };

  const ipc = window.electron?.cguAccept;
  if (ipc) {
    try {
      const saved = await ipc(record);
      if (saved?.id) {
        appendMirror(saved);
        return { success: true };
      }
    } catch {
      /* repli miroir — l’acceptation UI ne doit pas être perdue */
    }
  }
  appendMirror(record);
  return { success: true };
}
